import React, { useMemo } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
    PieChart, Pie, Cell, AreaChart, Area 
} from 'recharts';
import { FaChartPie, FaChartLine, FaChartBar, FaUsers, FaSuitcaseRolling } from 'react-icons/fa';

const ReportsAnalytics = ({ reportData, rawData, reportType, filters }) => {
    
    // 1. Process Data for Attendance Charts
    const attendanceStats = useMemo(() => {
        if (reportType !== 'attendance' || !reportData.length) return null;
        
        // Summary from Aggregated Data
        const summary = { present: 0, absent: 0, late: 0, halfday: 0 };
        reportData.forEach(record => {
            summary.present += (record.presentDays || 0);
            summary.absent += (record.absentDays || 0);
            summary.late += (record.lateMarks || 0);
            summary.halfday += (record.halfDays || 0);
        });
        
        const pieData = [
            { name: 'Present', value: summary.present, color: '#10b981' },
            { name: 'Absent', value: summary.absent, color: '#ef4444' }
        ];

        // Trends from Raw Records
        const dailyTrendMap = {};
        if (rawData && rawData.length) {
            rawData.forEach(record => {
                let dateObj;
                if (record.date && typeof record.date.toDate === 'function') {
                    dateObj = record.date.toDate();
                } else if (record.date) {
                    dateObj = new Date(record.date);
                }
                
                if (dateObj && !isNaN(dateObj)) {
                    const dateStr = dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                    // Only count present/engagement events for the trend
                    const status = String(record.status || '').toLowerCase();
                    if (!['absent', 'leave'].includes(status)) {
                        dailyTrendMap[dateStr] = (dailyTrendMap[dateStr] || 0) + 1;
                    }
                }
            });
        }

        const trendData = Object.keys(dailyTrendMap)
            .sort((a, b) => {
                const dayA = parseInt(a.split(' ')[0]);
                const dayB = parseInt(b.split(' ')[0]);
                return dayA - dayB;
            })
            .map(date => ({
                date,
                count: dailyTrendMap[date]
            }));

        return { summary, pieData, trendData };
    }, [reportData, rawData, reportType]);

    // 2. Process Data for Leave Charts
    const leaveStats = useMemo(() => {
        if (reportType !== 'leave' || !reportData.length) return null;

        const typeMap = {};
        const statusMap = {};

        reportData.forEach(leave => {
            const type = leave.leaveType || leave.type || 'Regular';
            const status = leave.status || 'Pending';
            typeMap[type] = (typeMap[type] || 0) + 1;
            statusMap[status] = (statusMap[status] || 0) + 1;
        });

        const barData = Object.keys(typeMap).map(type => ({
            name: type.charAt(0).toUpperCase() + type.slice(1),
            Requests: typeMap[type]
        }));

        const statusPie = Object.keys(statusMap).map(status => ({
            name: status.charAt(0).toUpperCase() + status.slice(1),
            value: statusMap[status]
        }));

        return { barData, statusPie };
    }, [reportData, reportType]);

    // 3. Process Data for Departmental/Movement Charts
    const movementStats = useMemo(() => {
        if (reportType !== 'employee-movement' && reportType !== 'department-wise') return null;

        const label = reportType === 'department-wise' ? 'Department' : 'Status';
        const statsMap = {};

        reportData.forEach(record => {
            const key = record.department || record.type || record.status || 'Other';
            statsMap[key] = (statsMap[key] || 0) + 1;
        });

        return Object.keys(statsMap).map(key => ({
            name: key,
            value: statsMap[key]
        }));
    }, [reportData, reportType]);

    if (!reportData || reportData.length === 0) return null;

    const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

    return (
        <div className="reports-analytics-section mt-4 mb-5">
            <h4 className="fw-black text-dark mb-4 d-flex align-items-center">
                <FaChartLine className="text-primary me-2" /> 
                Report Insights & Trends
            </h4>

            <Row className="g-4">
                {/* Module Specific Charts */}
                {attendanceStats && (
                    <>
                        <Col lg={4}>
                            <Card className="border-0 shadow-sm rounded-4 h-100">
                                <Card.Body className="p-4">
                                    <h6 className="fw-bold text-muted mb-4 d-flex align-items-center">
                                        <FaChartPie className="me-2 text-info" /> Attendance Ratio
                                    </h6>
                                    <div style={{ height: '250px' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={attendanceStats.pieData}
                                                    innerRadius={60}
                                                    outerRadius={80}
                                                    paddingAngle={5}
                                                    dataKey="value"
                                                >
                                                    {attendanceStats.pieData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <Tooltip />
                                                <Legend />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm rounded-4 h-100">
                                <Card.Body className="p-4">
                                    <h6 className="fw-bold text-muted mb-4 d-flex align-items-center">
                                        <FaChartLine className="me-2 text-primary" /> Daily Engagement Trend
                                    </h6>
                                    <div style={{ height: '250px' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={attendanceStats.trendData}>
                                                <defs>
                                                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1}/>
                                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                                                <Tooltip />
                                                <Area type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorCount)" />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </>
                )}

                {leaveStats && (
                    <>
                        <Col lg={7}>
                            <Card className="border-0 shadow-sm rounded-4 h-100">
                                <Card.Body className="p-4">
                                    <h6 className="fw-bold text-muted mb-4 d-flex align-items-center">
                                        <FaChartBar className="me-2 text-warning" /> Leave Categories Distribution
                                    </h6>
                                    <div style={{ height: '250px' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={leaveStats.barData}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                                                <Tooltip />
                                                <Bar dataKey="Requests" fill="#f59e0b" radius={[10, 10, 0, 0]} barSize={40} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col lg={5}>
                            <Card className="border-0 shadow-sm rounded-4 h-100">
                                <Card.Body className="p-4">
                                    <h6 className="fw-bold text-muted mb-4 d-flex align-items-center">
                                        <FaSuitcaseRolling className="me-2 text-success" /> Leave Approval Status
                                    </h6>
                                    <div style={{ height: '250px' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={leaveStats.statusPie}
                                                    cx="50%"
                                                    cy="50%"
                                                    labelLine={false}
                                                    outerRadius={80}
                                                    dataKey="value"
                                                >
                                                    {leaveStats.statusPie.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip />
                                                <Legend />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </>
                )}

                {movementStats && (
                    <Col lg={12}>
                        <Card className="border-0 shadow-sm rounded-4">
                            <Card.Body className="p-4">
                                <h6 className="fw-bold text-muted mb-4 d-flex align-items-center">
                                    <FaUsers className="me-2 text-primary" /> 
                                    {reportType === 'department-wise' ? 'Departmental Headcount Ratio' : 'Staff Movement Analytics'}
                                </h6>
                                <div style={{ height: '300px' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart 
                                            data={movementStats} 
                                            layout="vertical"
                                            margin={{ left: 40 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                                            <XAxis type="number" axisLine={false} tickLine={false} hide />
                                            <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fontWeight: 'bold' }} />
                                            <Tooltip />
                                            <Bar dataKey="value" fill="#3b82f6" radius={[0, 10, 10, 0]} barSize={25} label={{ position: 'right', fill: '#64748b', fontSize: 12 }} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                )}
            </Row>
        </div>
    );
};

export default ReportsAnalytics;
